
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { CaseForm, CaseFormData } from "./CaseForm";
import { createCase } from "@/services/cases";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface CreateCaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function CreateCaseDialog({ open, onOpenChange, onSuccess }: CreateCaseDialogProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  async function onSubmit(values: CaseFormData) {
    setIsSubmitting(true);
    setProgress(25);
    setError(null);
    
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        throw sessionError;
      }

      if (!session?.user?.id) {
        toast({
          title: "Autentiseringsfeil",
          description: "Du må være innlogget for å opprette en sak. Vennligst logg inn og prøv igjen.",
          variant: "destructive",
          duration: 3000,
        });
        onOpenChange(false);
        navigate('/login');
        return;
      }

      setProgress(50);
      console.log('Oppretter sak for bruker:', session.user.id);

      setProgress(75);
      const result = await createCase(values, session.user.id);

      if (!result) {
        throw new Error("Kunne ikke opprette sak");
      }

      setProgress(100);
      onSuccess();
      onOpenChange(false);

      setTimeout(() => {
        toast({
          title: "Suksess",
          description: "Din nye sak har blitt opprettet med en AI-generert arbeidsflyt.",
          duration: 2000,
        });
      }, 300);

    } catch (error) {
      console.error('Feil ved opprettelse av sak:', error);
      setError(error instanceof Error ? error.message : "Det oppstod en feil under opprettelse av saken");
      toast({
        title: "Feil ved opprettelse av sak",
        description: "Vennligst prøv igjen senere",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
      setProgress(0);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-2xl">
        <DialogHeader>
          <DialogTitle>Opprett ny sak</DialogTitle>
          <DialogDescription>
            Fyll ut skjemaet under for å opprette en ny sak. Systemet vil automatisk generere en arbeidsflyt basert på informasjonen du oppgir.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="h-[calc(85vh-100px)] pr-4">
          <div className="space-y-4">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Feil</AlertTitle>
                <AlertDescription>
                  {error}
                </AlertDescription>
              </Alert>
            )}
            {progress > 0 && (
              <div className="space-y-2 mb-4">
                <Progress value={progress} className="w-full" />
                <p className="text-sm text-muted-foreground">
                  {progress === 25 && "Initialiserer saksopprettelse..."}
                  {progress === 50 && "Verifiserer sesjon..."}
                  {progress === 75 && "Genererer AI-arbeidsflyt..."}
                  {progress === 100 && "Fullfører saksopprettelse..."}
                </p>
              </div>
            )}
            <CaseForm
              onSubmit={onSubmit}
              isSubmitting={isSubmitting}
              onCancel={() => onOpenChange(false)}
            />
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
